// Bucketing for runBulkAction: turns the settled per-purchase promises into the three outcomes a
// bulk run can have. Lives here rather than in the component so the classification — the part that
// decides what the user is told and what stays selected — can be tested.

export interface BulkRunItem {
  id: string;
  name: string;
}

export interface BulkRunFailure extends BulkRunItem {
  /** The rejection, kept so the caller can report it without this module knowing about Sentry. */
  reason: unknown;
}

export interface BulkRunOutcome {
  /** Items whose action went through: neither failed nor skipped. */
  changed: number;
  failed: BulkRunFailure[];
  skippedByReason: Record<string, BulkRunItem[]>;
}

/**
 * Buckets one settled result per item. A fulfilled string is a skip reason (the action's own
 * pre-flight checks return those); a rejection is a failure — unless `classifyRejection` names it
 * a skip, which is how a SERVER-side refusal of an expected state (e.g. a contract signed after
 * the page loaded) gets the same honest message as the client-side check that missed it, instead
 * of a generic "misslyckades".
 */
export function bucketSettledResults(
  results: PromiseSettledResult<unknown>[],
  items: BulkRunItem[],
  classifyRejection?: (reason: unknown) => string | undefined,
): BulkRunOutcome {
  const failed: BulkRunFailure[] = [];
  const skippedByReason: Record<string, BulkRunItem[]> = {};
  const skip = (reason: string, item: BulkRunItem) => {
    (skippedByReason[reason] ??= []).push(item);
  };

  results.forEach((result, i) => {
    const item = items[i];
    if (result.status === 'rejected') {
      const asSkip = classifyRejection?.(result.reason);
      if (asSkip) skip(asSkip, item);
      else failed.push({ ...item, reason: result.reason });
    } else if (typeof result.value === 'string') {
      skip(result.value, item);
    }
  });

  const skippedCount = Object.values(skippedByReason).reduce((sum, list) => sum + list.length, 0);
  return { changed: items.length - failed.length - skippedCount, failed, skippedByReason };
}

/**
 * What the selection should be after a run. Failures stay selected — retrying them is one click
 * instead of a re-hunt, and every runBulkAction caller is retry-safe (a purchase that actually
 * changed comes back as a skip on the second pass, not as a double-apply). A clean or fully
 * skipped run clears, so the action bar does not linger over nothing actionable.
 */
export function selectionAfterBulkRun(outcome: BulkRunOutcome): Set<string> | null {
  return outcome.failed.length > 0 ? new Set(outcome.failed.map(f => f.id)) : null;
}

/**
 * Whether a run ended with nothing said: no changes, no failures, and every skip silent. One path
 * genuinely reaches this — a concurrent edit turning every selected item into a silent no-op skip
 * (e.g. the co-worker was removed everywhere between page load and click) — and a bulk action that
 * ends in total silence reads as a bug, so the caller shows a "nothing changed" notice instead.
 *
 * `visibleReasons` is the set of skip reasons the caller has a message for; a run whose skips
 * include one of those already explained itself.
 */
export function needsNothingChangedNotice(
  outcome: BulkRunOutcome,
  visibleReasons: string[],
): boolean {
  if (outcome.changed > 0 || outcome.failed.length > 0) return false;
  return Object.keys(outcome.skippedByReason).every(reason => !visibleReasons.includes(reason));
}
